import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IntlCurrencyInput from "react-intl-currency-input";
import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDate, formatFreightValueDisplay } from "../mockData";
import { formatCarrierQueueWaitingTime, formatDistanceKm } from "../availableFreight.mapper";
import {
  HUB_TAB_AVAILABLE,
  HUB_TAB_IN_REVIEW,
} from "../carrierHub.mapper";
import {
  isPriceVisibilityMasked,
  isAnonymousShipperLabel,
  getAnonymousShipperBannerText,
} from "../../../util/freight/carrierPriceVisibility";
import {
  getPublishedPriceLabel,
  isBidFreight,
} from "../../../util/freight/contractingType";

const bidActionSx = {
  flex: 1,
  minHeight: 44,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: "none",
  backgroundColor: "#ff3100",
  color: "#fff",
  "&:hover": { backgroundColor: "#e02c00", boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#ff3100",
    color: "#fff",
    opacity: 0.5,
  },
};

const queueActionSx = {
  flex: 1,
  minHeight: 44,
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 14,
  boxShadow: "none",
  backgroundColor: "#012443",
  color: "#fff",
  "&:hover": { backgroundColor: "#01365f", boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#012443",
    color: "#fff",
    opacity: 1,
  },
};

const currencyConfig = {
  locale: "pt-BR",
  formats: {
    number: {
      BRL: {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
};

const CARRIER_RESPONSIBILITIES = [
  { label: "CT-e", hint: "Por sua conta" },
  { label: "Manifesto (MDF-e)", hint: "Por sua conta" },
  { label: "Seguro", hint: "Por sua conta" },
  { label: "Impostos", hint: "Por sua conta" },
  { label: "Vale Pedágio", hint: "Por sua conta" },
];

function SpecItem({ icon: Icon, label, value }) {
  return (
    <Box className="lances-detail-spec">
      <span className="lances-detail-spec__icon">
        <Icon fontSize="small" />
      </span>
      <Box>
        <Typography className="lances-detail-spec__label">{label}</Typography>
        <Typography className="lances-detail-spec__value">
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function formatOptionalMoney(value, pricingType) {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return formatFreightValueDisplay(value, pricingType);
}

/** Pedágio: ausência ou zero → R$ 0,00 (fretes sem pedágio são válidos). */
function formatTollMoney(value, pricingType) {
  const num = Number(value);
  const amount = Number.isFinite(num) && num > 0 ? num : 0;
  return formatFreightValueDisplay(amount, pricingType);
}

function parseToDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value?.toDate === "function") {
    const fromTs = value.toDate();
    return isValid(fromTs) ? fromTs : null;
  }
  const asString = String(value);
  const iso = parseISO(asString);
  if (isValid(iso)) return iso;
  const fallback = new Date(asString);
  return isValid(fallback) ? fallback : null;
}

function formatClock(value) {
  const date = parseToDate(value);
  if (!date) return "";
  return format(date, "HH:mm");
}

function formatPublishedHint(value) {
  const date = parseToDate(value);
  if (!date) return null;
  return `Publicado ${formatDistanceToNow(date, {
    addSuffix: true,
    locale: ptBR,
  })}`;
}

function formatRouteLabel(value) {
  return String(value || "")
    .replace(/\s*[-–]\s*/g, "/")
    .replace(/\s*\/\s*/g, "/")
    .toUpperCase();
}

function ValueTile({ label, value, hint }) {
  return (
    <Box className="lances-value-tile">
      <Typography className="lances-value-tile__label">{label}</Typography>
      <Typography className="lances-value-tile__value">{value}</Typography>
      {hint ? (
        <Typography className="lances-value-tile__hint">{hint}</Typography>
      ) : null}
    </Box>
  );
}

function buildCarrierHistory(freight, { isInQueue, hasBidThread }) {
  const items = [
    { key: "viewed", label: "Visualizou agora", time: "Agora", tone: "active" },
  ];

  if (freight?.bidSubmittedAt || freight?.yourBidAmount) {
    const clock = formatClock(freight.bidSubmittedAt);
    items.push({
      key: "bid",
      label: clock ? `Lance enviado às ${clock}` : "Lance enviado",
      time: clock || "—",
      tone: "done",
    });
  }

  if (freight?.counterAmount) {
    const clock = formatClock(freight.counterSubmittedAt);
    items.push({
      key: "counter",
      label: "Contraproposta do embarcador",
      time: clock || "—",
      tone: freight.canAcceptCounter ? "active" : "done",
    });
  }

  if (freight?.replyAmount) {
    const clock = formatClock(freight.replySubmittedAt);
    items.push({
      key: "reply",
      label: clock ? `Tréplica enviada às ${clock}` : "Tréplica enviada",
      time: clock || "—",
      tone: "done",
    });
  }

  if (
    hasBidThread &&
    (freight?.negotiationTurn === "shipper" ||
      (freight?.yourBidAmount && !freight?.counterAmount))
  ) {
    const clock = formatClock(
      freight.replySubmittedAt || freight.bidSubmittedAt,
    );
    items.push({
      key: "review",
      label: freight.replyAmount
        ? "Aguardando decisão final do embarcador"
        : "Em análise pelo embarcador",
      time: clock || "—",
      tone: "pending",
    });
  }

  if (!hasBidThread && isInQueue) {
    items.push({
      key: "queue",
      label: "Você está na fila aguardando contratação",
      time: formatClock(freight.carrierQueueJoinedAt) || "—",
      tone: "pending",
    });
  }

  return items;
}

export default function FreightDetailPanel({
  freight,
  loading,
  error,
  acceptingFreightId,
  activeTab = "available",
  onClose,
  onAcceptFreight,
  onSubmitBid,
  onAcceptCounter,
  onSubmitReply,
}) {
  const [bidAmount, setBidAmount] = useState(0);
  const [replyAmount, setReplyAmount] = useState(0);

  useEffect(() => {
    setBidAmount(Number(freight?.yourBidAmount) || 0);
    setReplyAmount(
      Number(freight?.counterAmount) || Number(freight?.yourBidAmount) || 0,
    );
  }, [freight?.id, freight?.yourBidAmount, freight?.counterAmount]);

  const historyItems = useMemo(
    () =>
      freight
        ? buildCarrierHistory(freight, {
            isInQueue: Boolean(freight.isInCarrierQueue),
            hasBidThread: Boolean(freight.yourBidAmount),
          })
        : [],
    [freight],
  );

  if (loading) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <CircularProgress size={32} sx={{ color: "#012443" }} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          Carregando detalhes...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="error" align="center">
          {error}
        </Typography>
        <Button variant="text" onClick={onClose} sx={{ mt: 2 }}>
          Fechar
        </Button>
      </Box>
    );
  }

  if (!freight) {
    return (
      <Box className="lances-detail-panel lances-detail-panel--empty">
        <Typography color="text.secondary" align="center">
          Selecione um frete para ver os detalhes
        </Typography>
      </Box>
    );
  }

  const isAccepting = acceptingFreightId === freight.id;
  const isInQueue = Boolean(freight.isInCarrierQueue);
  const isMasked = isPriceVisibilityMasked(freight);
  const isMyClient = Boolean(freight.isMyClient || freight.isAffiliateShipper);
  const isBid = isBidFreight(freight);
  const queueWaitingTime = isInQueue
    ? formatCarrierQueueWaitingTime(freight)
    : null;
  const shipperName =
    freight.priceVisibility?.displayShipperName || freight.shipper;
  const isAnonymousShipper = isAnonymousShipperLabel(shipperName);
  const displayPrice =
    freight.priceVisibility?.displayPrice ??
    freight.referenceValue ??
    freight.freightValue;
  const priceLabel = getPublishedPriceLabel(freight, { isMasked });
  const canManageQueue =
    activeTab === HUB_TAB_AVAILABLE || activeTab === HUB_TAB_IN_REVIEW;
  const lastBidValue = freight.yourBidAmount;
  const canSubmitBid = isBid && freight.canSubmitBid !== false && !isMasked;
  const canEnterQueue =
    !isBid && canManageQueue && !isMasked && !isInQueue;
  const canAcceptCounter = isBid && freight.canAcceptCounter === true;
  const canReplyBid = isBid && freight.canReplyBid === true;
  const hasBidThread = isBid && (Boolean(freight.yourBidAmount) || isInQueue);
  const showBidForm = canManageQueue && isBid && !canAcceptCounter;

  return (
    <Box
      className={[
        "lances-detail-panel",
        "lances-cargo",
        isMyClient && !isBid ? "lances-detail-panel--client" : "",
        isBid ? "lances-detail-panel--open" : "",
        isMasked && !isMyClient ? "lances-detail-panel--external" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Box className="lances-detail-panel__header">
        <Typography variant="h6" fontWeight={700}>
          Detalhes da carga
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="lances-detail-panel__content">
        {isInQueue ? (
          <Box className="lances-freight-card__candidate-strip lances-freight-card__candidate-strip--detail">
            Candidato a este frete
          </Box>
        ) : null}
        {isMasked && !isMyClient ? (
          <Box className="lances-freight-card__external-strip lances-freight-card__external-strip--detail">
            Valor Anunciado — somente visualização
          </Box>
        ) : null}
        {isAnonymousShipper ? (
          <Box className="lances-freight-card__anonymous-strip lances-freight-card__anonymous-strip--detail">
            {getAnonymousShipperBannerText(shipperName)}
          </Box>
        ) : null}

        <Typography className="lances-detail-panel__route-title">
          {formatRouteLabel(freight.origin)} → {formatRouteLabel(freight.destination)}
        </Typography>

        <Box className="lances-detail-route-visual" aria-hidden>
          <span className="lances-detail-route-visual__pin lances-detail-route-visual__pin--origin">
            <FmdGoodIcon />
          </span>
          <span className="lances-detail-route-visual__line" />
          <span className="lances-detail-route-visual__truck">
            <LocalShippingOutlinedIcon />
          </span>
          <span className="lances-detail-route-visual__line" />
          <span className="lances-detail-route-visual__pin lances-detail-route-visual__pin--destination">
            <FmdGoodIcon />
          </span>
        </Box>

        <Box className="lances-detail-spec-grid">
          <SpecItem
            icon={CalendarTodayOutlinedIcon}
            label="Data da coleta"
            value={formatDate(freight.collectionDate)}
          />
          <SpecItem
            icon={LocalShippingOutlinedIcon}
            label="Tipo de veículo"
            value={freight.vehicleType}
          />
          <SpecItem
            icon={CategoryOutlinedIcon}
            label="Tipo de carga"
            value={freight.cargoType}
          />
          <SpecItem
            icon={Inventory2OutlinedIcon}
            label="Carroceria"
            value={freight.bodyType}
          />
          <SpecItem
            icon={ScheduleOutlinedIcon}
            label="Prazo de pagamento"
            value={freight.paymentTermLabel}
          />
          <SpecItem
            icon={AccountBalanceOutlinedIcon}
            label="Modalidade"
            value={freight.modality || freight.contractingLabel}
          />
          <SpecItem
            icon={ScaleOutlinedIcon}
            label="Peso"
            value={freight.weight && freight.weight !== "—" ? freight.weight : "—"}
          />
          <SpecItem
            icon={RouteOutlinedIcon}
            label="Distância"
            value={formatDistanceKm(freight.distanceKm) || "—"}
          />
        </Box>

        <Typography variant="subtitle2" className="lances-detail-section-title lances-detail-section-title--first">
          Valores para esta carga
        </Typography>
        <Box className="lances-values-grid">
          <ValueTile
            label="Valor de referência"
            value={formatFreightValueDisplay(displayPrice, freight.pricingType)}
          />
          <ValueTile
            label="Pedágio"
            value={formatTollMoney(freight.tollAmount, freight.pricingType)}
            hint="Estimado"
          />
          <ValueTile
            label="Valor ANTT"
            value={formatOptionalMoney(freight.anttValue, freight.pricingType)}
            hint="Piso mínimo"
          />
          <ValueTile
            label="Meu último lance"
            value={formatOptionalMoney(lastBidValue, freight.pricingType)}
            hint={formatPublishedHint(freight.bidSubmittedAt)}
          />
        </Box>
        {!isBid ? (
          <Typography className="lances-values-note" variant="body2">
            {priceLabel}: {formatFreightValueDisplay(displayPrice, freight.pricingType)}
          </Typography>
        ) : null}
        {freight.counterAmount ? (
          <Typography className="lances-values-note" variant="body2">
            Contraproposta do embarcador:{" "}
            {formatOptionalMoney(freight.counterAmount, freight.pricingType)}
          </Typography>
        ) : null}

        <Typography className="lances-detail-section-title">
          Como você participa nesta carga
        </Typography>
        <Box className="lances-participate-card">
          <Box className="lances-participate-card__header">
            <ShieldOutlinedIcon className="lances-participate-card__shield" />
            <Box>
              <Typography className="lances-participate-card__subtitle">
                Responsabilidades da transportadora
              </Typography>
              <Typography className="lances-participate-card__intro">
                Ao aceitar esta carga, sua transportadora assume estas
                responsabilidades.
              </Typography>
            </Box>
          </Box>
          <Box className="lances-participate-grid">
            {CARRIER_RESPONSIBILITIES.map((item) => (
              <Box key={item.label} className="lances-participate-item">
                <CheckCircleIcon className="lances-participate-item__icon" />
                <Box>
                  <Typography className="lances-participate-item__label">
                    {item.label}
                  </Typography>
                  <Typography className="lances-participate-item__hint">
                    {item.hint}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {isBid && hasBidThread && freight.negotiationStatusLabel ? (
          <Box className="lances-freight-card__queue-hint-wrap" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {freight.negotiationStatusLabel}
            </Typography>
          </Box>
        ) : null}
        {!isBid && isInQueue ? (
          <Box className="lances-freight-card__queue-hint-wrap" sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Você está na fila aguardando contratação
            </Typography>
            {queueWaitingTime ? (
              <Typography variant="body2" color="text.secondary">
                há {queueWaitingTime}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {showBidForm ? (
          <Box className="lances-your-bid">
            <Typography className="lances-detail-section-title">
              Meu lance
            </Typography>
            <Box className="lances-bid-fields">
              <Box className="lances-bid-fields__amount">
                <Typography className="lances-bid-fields__label">
                  Quanto você deseja receber
                </Typography>
                <IntlCurrencyInput
                  currency="BRL"
                  config={currencyConfig}
                  value={bidAmount || 0}
                  disabled={isAccepting || !canSubmitBid}
                  onChange={(_, value) => setBidAmount(value)}
                  className="form-control lances-bid-input"
                />
              </Box>
              <Box className="lances-bid-fields__term">
                <Typography className="lances-bid-fields__label">
                  Prazo de recebimento:
                </Typography>
                <Box
                  className="lances-bid-term-input lances-bid-term-input--readonly"
                  aria-label="Prazo de recebimento"
                >
                  <CalendarTodayOutlinedIcon fontSize="small" />
                  <span>
                    {freight.paymentTermLabel || "Não informado"}
                  </span>
                </Box>
              </Box>
            </Box>
            <Typography className="lances-bid-help">
              O sistema calculará automaticamente o valor final que será
              apresentado ao embarcador, taxas da plataforma e impostos.
            </Typography>
          </Box>
        ) : null}

        {canAcceptCounter ? (
          <Box className="lances-your-bid">
            <Typography className="lances-detail-section-title">
              Tréplica (1x)
            </Typography>
            <IntlCurrencyInput
              currency="BRL"
              config={currencyConfig}
              value={replyAmount || 0}
              disabled={isAccepting || !canReplyBid}
              onChange={(_, value) => setReplyAmount(value)}
              className="form-control lances-bid-input"
            />
          </Box>
        ) : null}

        {canManageQueue ? (
          <Box className="lances-detail-actions">
            <Button
              variant="contained"
              disableElevation
              className="lances-cargo-btn lances-cargo-btn--bid"
              sx={bidActionSx}
              disabled={
                isAccepting ||
                (canAcceptCounter ? !canReplyBid : !canSubmitBid)
              }
              onClick={() => {
                if (canAcceptCounter) {
                  onSubmitReply?.(freight, replyAmount);
                  return;
                }
                onSubmitBid?.(freight, bidAmount);
              }}
            >
              {isAccepting ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : canAcceptCounter ? (
                "Enviar tréplica"
              ) : (
                "Enviar lance"
              )}
            </Button>
            {canAcceptCounter ? (
              <Button
                variant="contained"
                disableElevation
                className="lances-cargo-btn lances-cargo-btn--queue"
                sx={queueActionSx}
                disabled={isAccepting}
                onClick={() => onAcceptCounter?.(freight)}
              >
                Aceitar contraproposta
              </Button>
            ) : (
              <Button
                variant="contained"
                disableElevation
                className={
                  isInQueue && !isBid
                    ? "lances-cargo-btn lances-cargo-btn--leave"
                    : "lances-cargo-btn lances-cargo-btn--queue"
                }
                sx={
                  isInQueue && !isBid
                    ? {
                        ...queueActionSx,
                        backgroundColor: "#fff",
                        color: "#15803d",
                        border: "1px solid #15803d",
                        "&:hover": { backgroundColor: "#f0fdf4" },
                      }
                    : queueActionSx
                }
                disabled={
                  isAccepting ||
                  isBid ||
                  isMasked ||
                  (!canEnterQueue && !(isInQueue && !isBid))
                }
                onClick={() => {
                  if (isBid || (isMasked && !isInQueue)) return;
                  onAcceptFreight(freight);
                }}
              >
                {isAccepting && !isBid ? (
                  <CircularProgress
                    size={20}
                    sx={{ color: isInQueue ? "#15803d" : "#fff" }}
                  />
                ) : isInQueue && !isBid ? (
                  "Sair da fila"
                ) : (
                  "Entrar na fila"
                )}
              </Button>
            )}
          </Box>
        ) : null}

        <Box className="lances-history">
          <Typography className="lances-detail-section-title">
            Meu histórico nesta carga
          </Typography>
          <Box className="lances-history-list">
            {historyItems.map((item) => (
              <Box
                key={item.key}
                className={`lances-history-item lances-history-item--${item.tone}`}
              >
                <span className="lances-history-item__dot" />
                <Typography className="lances-history-item__label">
                  {item.label}
                </Typography>
                <Typography className="lances-history-item__time">
                  {item.time}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
